/**
 * StatementDialog
 * ───────────────
 * Shared "Send Client Statement" dialog — pixel-and-behavior parity
 * with the legacy version in `App.js::BillingTab` so office staff
 * have the same workflow whether they launch the dialog from the
 * patient chart Billing tab OR from the new V2 Client Bills tab.
 *
 * Feb 2026 — user request: "the send statement dialog from the client
 * bill tab should match the one you see from the client billing tab".
 *
 * Self-contained: owns its own internal state for CC emails, preview,
 * history, and loading flags.  Parents pass `open`, `onClose`, and a
 * `client` ({ id, full_name, email }).  The billing summary
 * (patient_balance / insurance_balance) is fetched lazily on open so
 * the dialog can be dropped anywhere without the parent having to
 * pre-fetch.
 *
 * Endpoints used (already exist):
 *   - GET  /api/billing/clients/{id}/billing-summary
 *   - POST /api/clients/{id}/preview-statement
 *   - POST /api/clients/{id}/send-statement
 *   - GET  /api/clients/{id}/statement-history
 *
 * Print / Save as PDF uses the same hidden-iframe strategy the legacy
 * dialog ships with — bypasses pop-up blockers without requiring any
 * extra permissions.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText,
  Eye,
  Send,
  Printer,
  Clock,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StatementDialog({
  open, onClose, client,
  // Jun 2026 — optimistic "last statement sent" support.  Parents may
  // stamp the date the moment Send is clicked (onOptimisticSent),
  // reconcile with the server result (onSentConfirmed) and revert if
  // the send ultimately fails or is cancelled (onSendFailed).
  onOptimisticSent, onSentConfirmed, onSendFailed,
}) {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [statementPreview, setStatementPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Uncontrolled CC field — typing here used to freeze the legacy
  // dialog because every keystroke triggered an App-wide re-render.
  // The ref approach preserves that fix locally.
  const ccRef = useRef(null);
  const [ccShadow, setCcShadow] = useState('');

  const clientId = client?.id;

  // ── Fetch billing summary lazily once the dialog opens for a real
  // client.  We deliberately re-fetch each open so an outstanding
  // balance that was paid in another tab is reflected immediately.
  const fetchSummary = useCallback(async () => {
    if (!clientId) return;
    setSummaryLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/clients/${clientId}/billing-summary`,
      );
      setSummary(data || null);
    } catch (err) {
      // Soft-fail — summary is informational; user can still preview/send.
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (open && clientId) {
      fetchSummary();
    }
    if (!open) {
      // Reset on close so re-opening for a different client doesn't
      // flash stale state.
      setSummary(null);
      setStatementPreview(null);
      setHistory(null);
      setShowHistory(false);
      setCcShadow('');
      if (ccRef.current) ccRef.current.value = '';
    }
  }, [open, clientId, fetchSummary]);

  const parseCc = () => {
    const raw = ccRef.current?.value ?? ccShadow ?? '';
    return raw
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter((s) => s && s.includes('@'));
  };

  const handlePreview = async () => {
    if (!client?.email) {
      toast.error('Client does not have an email on file. Use Print / Save as PDF instead.');
      // Allow preview path to still run when no email — backend gates
      // only on the send endpoint, not on preview.
    }
    setPreviewing(true);
    try {
      const { data } = await axios.post(
        `${API}/clients/${clientId}/preview-statement`,
        { cc_emails: parseCc() },
      );
      setStatementPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!client?.email) {
      toast.error('Client does not have an email on file');
      return;
    }
    setSending(true);
    const optimisticSentAt = new Date().toISOString();
    if (typeof onOptimisticSent === 'function') onOptimisticSent(optimisticSentAt);
    let succeeded = false;
    try {
      let data;
      try {
        ({ data } = await axios.post(
          `${API}/clients/${clientId}/send-statement`,
          { cc_emails: parseCc() },
        ));
      } catch (err) {
        // Jun 2026 — payment-link generation failed (e.g. Square 401).
        // Backend now refuses to send silently; offer a conscious
        // "send anyway" without the Pay Now button.
        const det = err?.response?.data?.detail;
        if (typeof det === 'string' && det.startsWith('PAYMENT_LINK_FAILED:')) {
          const msg = det.replace('PAYMENT_LINK_FAILED: ', '');
          if (!window.confirm(`${msg}\n\nSend the statement anyway WITHOUT a Pay Now button?`)) {
            return;
          }
          ({ data } = await axios.post(
            `${API}/clients/${clientId}/send-statement`,
            { cc_emails: parseCc(), force_send_without_payment_link: true },
          ));
        } else {
          throw err;
        }
      }
      const ccList = parseCc();
      const ccNote = ccList.length ? ` (cc: ${ccList.join(', ')})` : '';
      toast.success(`Statement sent to ${client.email}${ccNote}`);
      if (data?.payment_link) {
        toast.success(`Payment link: ${data.statement_number}`, { duration: 5000 });
      }
      succeeded = true;
      if (typeof onSentConfirmed === 'function') {
        onSentConfirmed({ ...(data || {}), optimistic_sent_at: optimisticSentAt });
      }
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send statement');
    } finally {
      setSending(false);
      if (!succeeded && typeof onSendFailed === 'function') onSendFailed();
    }
  };

  const handlePrint = () => {
    // Hidden-iframe print — bypasses pop-up blockers.  Same code path
    // the legacy dialog uses; lifted verbatim so the print quirks the
    // biller has worked around stay identical across both entry
    // points.
    if (!statementPreview?.html_body) {
      toast.error('Generate a preview first');
      return;
    }
    try {
      const frame = document.createElement('iframe');
      frame.style.position = 'fixed';
      frame.style.right = '0';
      frame.style.bottom = '0';
      frame.style.width = '0';
      frame.style.height = '0';
      frame.style.border = '0';
      document.body.appendChild(frame);

      const cleanup = () => {
        try { document.body.removeChild(frame); } catch (_) { /* noop */ }
      };

      frame.onload = () => {
        setTimeout(() => {
          try {
            const win = frame.contentWindow;
            win.focus();
            win.print();
            setTimeout(cleanup, 1000);
          } catch (e) {
            cleanup();
            toast.error('Unable to open print dialog');
          }
        }, 200);
      };

      const doc = frame.contentDocument || frame.contentWindow?.document;
      if (!doc) {
        cleanup();
        toast.error('Unable to prepare print frame');
        return;
      }
      doc.open();
      doc.write(statementPreview.html_body || '');
      doc.close();
    } catch (e) {
      toast.error('Unable to open print dialog');
    }
  };

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null && clientId) {
      setHistoryLoading(true);
      try {
        const resp = await axios.get(
          `${API}/clients/${clientId}/statement-history?limit=50`,
        );
        setHistory(resp.data?.statements || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load statement history');
        setHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className={statementPreview ? 'max-w-6xl max-h-[92vh] overflow-y-auto' : 'max-w-xl'}
        data-testid="statement-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Send Client Statement
          </DialogTitle>
          <DialogDescription>
            Send an itemized statement to {client?.full_name || '—'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary cards — mirror the legacy dialog's blue panel. */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Client Balance</p>
                <p className="text-xl font-bold text-blue-700" data-testid="statement-dialog-patient-balance">
                  ${summary?.financials?.patient_balance?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Insurance Pending</p>
                <p className="text-xl font-bold text-gray-700" data-testid="statement-dialog-insurance-balance">
                  ${summary?.financials?.insurance_balance?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
            {summaryLoading && (
              <p className="text-xs text-gray-500 mt-2">Loading summary…</p>
            )}
          </div>

          {/* Email destination + CC */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Send to:</strong> {client?.email || 'No email on file'}
            </p>
          </div>

          <div>
            <Label htmlFor="statement-dialog-cc" className="text-sm font-medium">
              CC (optional)
            </Label>
            <Input
              id="statement-dialog-cc"
              type="text"
              placeholder="finance@yourpractice.com, owner@yourpractice.com"
              ref={ccRef}
              defaultValue={ccShadow}
              onBlur={(e) => {
                setCcShadow(e.target.value);
                if (statementPreview) setStatementPreview(null);
              }}
              className="mt-1"
              data-testid="statement-dialog-cc-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Add yourself or a billing coworker to verify delivery.
              Separate multiple addresses with commas.
            </p>
          </div>

          {!client?.email && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800"
              data-testid="statement-dialog-no-email-warning"
            >
              This client doesn&apos;t have an email on file.  You can still <strong>Preview</strong> and <strong>Print / Save as PDF</strong>, but <strong>Send Statement</strong> is disabled until an email is added in Manage Insurances.
            </div>
          )}

          {/* Preview pane */}
          {statementPreview && (
            <div className="border rounded-lg overflow-hidden" data-testid="statement-dialog-preview">
              <div className="bg-gray-50 border-b px-3 py-2 text-xs font-mono space-y-0.5">
                <div>
                  <span className="text-gray-500">To:</span>{' '}
                  <span className="font-semibold">{(statementPreview.to || []).join(', ')}</span>
                </div>
                {statementPreview.cc?.length > 0 && (
                  <div>
                    <span className="text-gray-500">Cc:</span>{' '}
                    <span className="font-semibold">{statementPreview.cc.join(', ')}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Subject:</span>{' '}
                  <span className="font-semibold">{statementPreview.subject}</span>
                </div>
              </div>
              <iframe
                title="Statement preview"
                srcDoc={statementPreview.html_body}
                sandbox="allow-same-origin"
                style={{ width: '100%', height: '60vh', border: 'none', display: 'block' }}
                data-testid="statement-dialog-preview-iframe"
              />
              <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 flex items-center justify-between gap-3">
                <span className="text-xs text-amber-800">
                  Preview only — payment link will be regenerated on send.
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={handlePrint}
                  data-testid="statement-dialog-print-btn"
                >
                  <Printer className="h-3.5 w-3.5 mr-1.5" />
                  Print / Save as PDF
                </Button>
              </div>
            </div>
          )}

          {/* Statement History */}
          <div className="border rounded-lg" data-testid="statement-dialog-history">
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-gray-50"
              onClick={toggleHistory}
              data-testid="statement-dialog-history-toggle"
            >
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                Statement History
                {history !== null && (
                  <span className="text-xs text-gray-500 font-normal">({history.length})</span>
                )}
              </span>
              <span className="text-xs text-gray-400">
                {showHistory ? 'Hide' : 'Show'}
              </span>
            </button>
            {showHistory && (
              <div className="border-t" data-testid="statement-dialog-history-list">
                {historyLoading ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mx-auto mb-2"></div>
                    Loading history…
                  </div>
                ) : history && history.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No statements have been sent to this client yet.
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto divide-y">
                    {(history || []).map((log) => {
                      const sentAt = log.sent_at
                        ? new Date(log.sent_at).toLocaleString()
                        : '—';
                      const ccList = (log.cc_emails || []).filter(Boolean);
                      return (
                        <div
                          key={log.id || log.statement_number}
                          className="px-3 py-2 text-xs space-y-0.5"
                          data-testid={`statement-dialog-history-row-${log.statement_number || log.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-gray-800">
                              {log.manual ? (
                                <>
                                  <span className="inline-block rounded bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-px text-[10px] font-semibold mr-1">MANUAL</span>
                                  {{ mail: 'Mailed', email: 'Emailed', in_person: 'In person', portal: 'Portal', other: 'Other' }[log.method] || 'Logged'}
                                </>
                              ) : log.statement_number}
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="text-gray-500">{sentAt}</span>
                              {!log.manual && log.payment_link && (
                                <button
                                  type="button"
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                  title={`Copy this statement's payment link so you can resend it manually:\n${log.payment_link}`}
                                  onClick={() => {
                                    navigator.clipboard.writeText(log.payment_link)
                                      .then(() => toast.success('Payment link copied to clipboard'))
                                      .catch(() => window.prompt('Copy the payment link:', log.payment_link));
                                  }}
                                  data-testid={`statement-copy-pay-link-${log.statement_number || log.id}`}
                                >
                                  Copy pay link
                                </button>
                              )}
                              {log.manual && (
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-red-600"
                                  title="Delete this manual log entry"
                                  onClick={async () => {
                                    try {
                                      await axios.delete(`${API}/statement-logs/${log.id}`);
                                      setHistory((h) => (h || []).filter((x) => x.id !== log.id));
                                    } catch (e2) {
                                      toast.error(e2?.response?.data?.detail || 'Could not delete');
                                    }
                                  }}
                                  data-testid={`manual-statement-delete-${log.id}`}
                                >
                                  ✕
                                </button>
                              )}
                            </span>
                          </div>
                          {log.manual ? (
                            log.note ? <div className="text-gray-600 italic">{log.note}</div> : null
                          ) : (
                          <div className="text-gray-600">
                            To: <span className="font-mono">{log.client_email || '—'}</span>
                            {ccList.length > 0 && (
                              <> · Cc: <span className="font-mono">{ccList.join(', ')}</span></>
                            )}
                          </div>
                          )}
                          {!log.manual && (
                          <>
                          <div className="flex items-center justify-between gap-2 text-gray-600">
                            <span>
                              Balance:{' '}
                              <span className="font-semibold text-blue-700">
                                ${(log.patient_balance || 0).toFixed(2)}
                              </span>
                              {' · '}
                              Charges: {log.charges_count ?? '—'}
                            </span>
                            <span className="text-gray-500">
                              by {log.sent_by_name || log.sent_by || '—'}
                            </span>
                          </div>
                          {log.payment_link && (
                            <a
                              href={log.payment_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline break-all"
                            >
                              Payment link ↗
                            </a>
                          )}
                          </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 mt-6 pt-4 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={() => onClose?.()}
            data-testid="statement-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={previewing || sending}
            data-testid="statement-dialog-preview-btn"
          >
            {previewing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                Loading…
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-2" />
                {statementPreview ? 'Refresh Preview' : 'Preview'}
              </>
            )}
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !client?.email}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="statement-dialog-send"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Statement
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
